// Tiny i18n context. `t(key, params?)` resolves against the active language;
// `locale` is the BCP-47 tag for Intl date/number formatting. The kiosk
// starts in Spanish and App.tsx resets it back to Spanish whenever the flow
// returns to idle, so the next visitor always sees the default.

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { type Lang, LOCALE, type StringKey, translate } from "./strings";

export type Translate = (
  key: StringKey,
  params?: Record<string, string | number>
) => string;

type LangContextValue = {
  lang: Lang;
  locale: string;
  setLang: (lang: Lang) => void;
  t: Translate;
};

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>("es");
  const value = useMemo<LangContextValue>(
    () => ({
      lang,
      locale: LOCALE[lang],
      setLang,
      t: (key, params) => translate(lang, key, params),
    }),
    [lang]
  );
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    throw new Error("useLang must be used within <LangProvider>");
  }
  return ctx;
}
